import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Sparkles, PenTool, Camera, ArrowRight, CheckCircle } from "lucide-react";

const LegacyProjects = () => {
  return (
    <>
      <Helmet>
        <title>Legacy & Life Review Projects Columbia, MO | Serenity Passage</title>
        <meta
          name="description"
          content="Create meaningful legacy projects in Columbia, Missouri. Letters, guided life reflections, and unique keepsakes. End-of-life doula services preserving your story for loved ones in Mid-Missouri."
        />
        <meta
          name="keywords"
          content="legacy projects Columbia MO, life review Mid-Missouri, ethical will, life story, end-of-life doula Columbia Missouri, memory preservation"
        />
        <link rel="canonical" href="https://www.serenitypassage.net/legacy-projects" />
      </Helmet>

      {/* Hero Banner */}
      <section className="bg-gradient-to-b from-rose-100 to-rose-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-serif text-gray-800 text-center mb-4">
            Legacy & Life Review
          </h1>
          <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto">
            Creating meaningful keepsakes that preserve your story for generations
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 text-lg leading-relaxed mb-8">
              Your life story matters. Legacy projects offer a beautiful way to reflect on your journey, capture your wisdom, and create lasting gifts for those you love. I guide you through this meaningful process with care and compassion.
            </p>

            <h2 className="text-2xl font-serif text-gray-800 mb-6 flex items-center gap-3">
              <Sparkles className="w-7 h-7 text-rose-600" />
              Legacy Projects
            </h2>

            <p className="text-gray-700 mb-6">
              Help identify meaningful legacy projects. Projects vary per client as each person is unique, as are their life experiences. Some examples include:
            </p>

            <ul className="space-y-4 mb-12">
              {[
                "A memory box for family and friends to contribute special moments",
                "Choosing gifts from around your home and distributing to your loved ones along with a note about why you chose that item for them",
                "Sew a quilt created out of your favorite clothes",
                "Compile a recipe book of your favorite meals",
                "Planting a tree or a garden",
                "Donate to a cause, fundraising event or establish a foundation",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-rose-500 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <h2 className="text-2xl font-serif text-gray-800 mb-6 flex items-center gap-3">
              <PenTool className="w-7 h-7 text-rose-600" />
              Letters & Ethical Wills
            </h2>

            <p className="text-gray-700 mb-6">
              Write heartfelt letters to loved ones or create an ethical will that shares your values, life lessons, and hopes for future generations.
            </p>

            <ul className="space-y-4 mb-12">
              {[
                "Personal letters to children, grandchildren, or loved ones",
                "Ethical wills sharing your values and wisdom",
                "Birthday or milestone letters for future occasions",
                "Guidance on what to include and how to express yourself",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-rose-500 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <h2 className="text-2xl font-serif text-gray-800 mb-6 flex items-center gap-3">
              <Camera className="w-7 h-7 text-rose-600" />
              Life Review Sessions
            </h2>

            <p className="text-gray-700 mb-6">
              Life review is a therapeutic process of reflecting on your life's journey. These guided sessions help you find meaning, resolve regrets, and celebrate your accomplishments.
            </p>

            <ul className="space-y-4 mb-12">
              {[
                "One-on-one guided reflection sessions",
                "Processing and integrating life experiences",
                "Finding meaning and peace in your story",
                "Recording video or audio messages if desired",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-rose-500 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-rose-50 to-rose-100 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-serif text-gray-800 mb-4">
              Your Story Deserves to Be Told
            </h3>
            <p className="text-gray-600 mb-6">
              Your first 30-minute consultation is complimentary.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-8 py-4 rounded-full font-medium transition-colors duration-200 shadow-lg hover:shadow-xl"
            >
              Schedule a Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default LegacyProjects;
